import { AlertCircle, CheckCircle2Icon, InfoIcon } from 'lucide-react'

import { cn } from '../lib/utils'
import { Subtitle } from './subtitle'

interface InfoBoxProps {
    infoType: 'success' | 'warning' | 'error' | 'info'
    message?: string
    className?: string
    children?: React.ReactNode
    'data-testid'?: string
}

export const InfoBox = ({ message, infoType, className, children, 'data-testid': dataTestId }: InfoBoxProps) => {
    const renderIcon = (infoType: string) => {
        switch (infoType) {
            case 'success':
                return (
                    <CheckCircle2Icon
                        className="text-lp-dark1 stroke-lp-dark1 w-4 h-4"
                        aria-hidden="true"
                    />
                )
            case 'warning':
                return (
                    <AlertCircle
                        className="text-lp-dark1 stroke-lp-dark1 w-4 h-4"
                        aria-hidden="true"
                    />
                )
            case 'error':
                return (
                    <AlertCircle
                        className="text-lp-dark1 stroke-lp-dark1 w-4 h-4"
                        aria-hidden="true"
                    />
                )
            case 'info':
                return (
                    <InfoIcon
                        className="text-lp-dark1 stroke-lp-dark1 w-4 h-4"
                        aria-hidden="true"
                    />
                )
            default:
                return (
                    <AlertCircle
                        className="text-lp-dark1 stroke-lp-dark1 w-4 h-4"
                        aria-hidden="true"
                    />
                )
        }
    }

    const getBackgroundColor = (infoType: string) => {
        switch (infoType) {
            case 'success':
                return 'bg-lp-success'
            case 'warning':
                return 'bg-lp-warning'
            case 'error':
                return 'bg-lp-error'
            case 'info':
                return 'bg-lp-info'
            default:
                return 'bg-lp-error-disclaimer'
        }
    }

    if (!message && !children) return null

    return (
        <div
            className={cn(
                'flex w-full px-4 py-2 rounded-lg gap-2 items-start',
                getBackgroundColor(infoType),
                className,
            )}
            role={infoType === 'error' ? 'alert' : 'status'}
            aria-live={infoType === 'error' ? 'assertive' : 'polite'}
            data-testid={dataTestId}
        >
            <div className="shrink-0">{renderIcon(infoType)}</div>
            {children ??
                (message && (
                    <Subtitle
                        testId={dataTestId ? `${dataTestId}-text` : undefined}
                        className="text-left text-sm mb-0"
                        dangerouslySetInnerHTML={{ __html: message }}
                    />
                ))}
        </div>
    )
}
